import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShoppingBag } from "lucide-react";
import { api, imgUrl, money } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { toast } from "sonner";

/**
 * <RecommendationsRail variant="cart" />     → Cart-aware recs
 * <RecommendationsRail variant="popular" />  → Empty-cart / cold-start
 * <RecommendationsRail variant="product" productId="..." />
 *                                            → Product-detail / drawer
 *
 * Renders a horizontal scrollable rail of product cards with quick-add.
 * Loads its own data; safe to drop anywhere. Returns null when there
 * are no results (e.g. a tenant with zero products) so it never leaves
 * an empty section behind.
 */
export default function RecommendationsRail({
  variant = "cart",
  productId,
  title,
  limit = 8,
  onAdded,
  className = "",
  "data-testid": testid = "recommendations-rail",
}) {
  const [items, setItems] = useState([]);
  const [strategy, setStrategy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(null);
  const { addToCart } = useCart();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        let url = "/recommendations/popular";
        if (variant === "cart") url = "/recommendations/cart";
        else if (variant === "product" && productId) url = `/recommendations/product/${productId}`;
        const { data } = await api.get(url, { params: { limit } });
        if (!cancelled) {
          setItems(data?.items || []);
          setStrategy(data?.strategy || null);
        }
      } catch (_e) {
        if (!cancelled) setItems([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [variant, productId, limit]);

  const quickAdd = async (p) => {
    setAdding(p.id);
    try {
      // No size selection from the rail — the product page is where
      // sizing happens. For products with required sizes the add still
      // works (backend either uses default / first available size or
      // returns 400 which we surface as a toast).
      await addToCart(p.id, "", 1, { name: p.title, image: imgUrl(p.image || (p.images || [])[0]) });
      toast.success(`${p.title} added to bag`);
      onAdded?.(p);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not add to cart";
      toast.error(typeof msg === "string" ? msg : "Could not add to cart");
    } finally {
      setAdding(null);
    }
  };

  if (!loading && items.length === 0) return null;

  // Default title per variant; consumers can override.
  const defaultTitle =
    variant === "cart"
      ? "You may also like"
      : variant === "product"
      ? "Frequently bought together"
      : "Popular right now";

  return (
    <section className={`mt-10 ${className}`} data-testid={testid} data-strategy={strategy || ""}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-2xl" data-testid={`${testid}-heading`}>
          {title || defaultTitle}
        </h2>
        {strategy && (
          <span className="text-[10px] text-[hsl(var(--kb-muted))] uppercase tracking-wider hidden md:inline" data-testid={`${testid}-strategy`}>
            {strategy.replace(/_/g, " ")}
          </span>
        )}
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm text-[hsl(var(--kb-muted))]">
          <Loader2 className="animate-spin" size={14} /> Loading suggestions…
        </div>
      ) : (
        <div
          className="flex gap-4 overflow-x-auto pb-4 -mx-4 px-4 snap-x snap-mandatory scrollbar-thin"
          data-testid={`${testid}-scroller`}
        >
          {items.map((p) => {
            const img = (p.image || (p.images || [])[0]);
            const compare = Number(p.compare_price || 0);
            const price = Number(p.price || 0);
            const off = compare > price ? Math.round(((compare - price) / compare) * 100) : 0;
            return (
              <div
                key={p.id}
                className="snap-start flex-shrink-0 w-[160px] sm:w-[200px] bg-white border border-[hsl(var(--kb-border))] rounded overflow-hidden hover:shadow-md transition-shadow"
                data-testid={`${testid}-card-${p.id}`}
              >
                <Link to={`/product/${p.slug || p.id}`} className="block group">
                  <div className="relative aspect-[3/4] bg-[hsl(var(--kb-surface-alt))] overflow-hidden">
                    {img ? (
                      <img
                        src={imgUrl(img)}
                        alt={p.title}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[hsl(var(--kb-muted))]">
                        <ShoppingBag size={28} />
                      </div>
                    )}
                    {off > 0 && (
                      <div className="absolute top-2 left-2 text-[10px] bg-red-600 text-white px-1.5 py-0.5 rounded">
                        {off}% OFF
                      </div>
                    )}
                  </div>
                  <div className="p-2.5 space-y-1">
                    <div className="text-xs font-medium line-clamp-2 leading-tight" title={p.title}>
                      {p.title}
                    </div>
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-sm font-semibold">{money(price)}</span>
                      {compare > price && (
                        <span className="text-[10px] line-through text-[hsl(var(--kb-muted))]">{money(compare)}</span>
                      )}
                    </div>
                  </div>
                </Link>
                <button
                  onClick={() => quickAdd(p)}
                  disabled={adding === p.id}
                  className="w-full text-[11px] py-1.5 border-t border-[hsl(var(--kb-border))] hover:bg-[hsl(var(--kb-surface-alt))] transition-colors disabled:opacity-50 flex items-center justify-center gap-1"
                  data-testid={`${testid}-add-btn-${p.id}`}
                >
                  {adding === p.id ? <Loader2 size={11} className="animate-spin" /> : <ShoppingBag size={11} />}
                  {adding === p.id ? "Adding…" : "Add to cart"}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
